/**
 * What the Assignment Setup screens pick from: the team (people and AI agents), departments,
 * workflows, and the business's WhatsApp numbers / Facebook pages.
 */
import { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import type { AssignmentRuleChannel } from '../../services/api';

export interface Option { id: string; label: string }
export interface ChannelOption extends Option { channel: AssignmentRuleChannel }
export interface FlowOption extends Option { published: boolean; active: boolean }

export interface RuleOptions {
  users: Option[];
  departments: Option[];
  flows: FlowOption[];
  numbers: ChannelOption[];
  loading: boolean;
}

export function useRuleOptions(): RuleOptions {
  const [state, setState] = useState<RuleOptions>({ users: [], departments: [], flows: [], numbers: [], loading: true });

  useEffect(() => {
    let alive = true;
    const businessId = apiService.getBusinessIdFromToken();
    const users = businessId
      ? apiService.getBusinessUsers(businessId).then((res) => (res.data || [])
        .filter((u) => u.userStatus === 'active')
        .map((u) => ({ id: u.userId, label: `${u.user?.firstName || ''} ${u.user?.lastName || ''}`.trim() || u.user?.email || u.userId })))
        .catch(() => [] as Option[])
      : Promise.resolve([] as Option[]);
    const departments = businessId
      ? apiService.getDepartments(businessId).then((res) => (res.data || []).map((d: any) => ({ id: String(d._id || d.id), label: d.name }))).catch(() => [] as Option[])
      : Promise.resolve([] as Option[]);
    const flows = apiService.getFlows({ limit: 1000, skip: 0 }).then((res) => {
      const rows: any[] = Array.isArray(res.data) ? res.data : res.data?.items || [];
      return rows.map((f) => ({ id: String(f._id || f.id), label: f.name || 'Workflow', published: !!f.publishedVersionId, active: f.isActive !== false }));
    }).catch(() => [] as FlowOption[]);
    const numbers = apiService.getWhatsAppPhoneNumbers().then(async (res) => {
      const rows: any[] = Array.isArray(res.data) ? res.data : [];
      const wa: ChannelOption[] = rows.filter((p) => p.phoneNumberId).map((p) => ({ id: String(p.phoneNumberId), label: p.displayPhoneNumber || p.verifiedName || p.phoneNumberId, channel: 'whatsapp' }));
      const pages = await apiService.getFacebookPages().catch(() => null);
      const fb: ChannelOption[] = (Array.isArray(pages?.data) ? pages!.data : []).filter((p: any) => p.pageId || p.id)
        .map((p: any) => ({ id: String(p.pageId || p.id), label: p.name || p.pageName || p.pageId, channel: 'facebook_messenger' }));
      return [...wa, ...fb];
    }).catch(() => [] as ChannelOption[]);

    Promise.all([users, departments, flows, numbers]).then(([u, d, f, n]) => {
      if (alive) setState({ users: u, departments: d, flows: f, numbers: n, loading: false });
    });
    return () => { alive = false; };
  }, []);

  return state;
}

export const labelOf = (list: Option[], id?: string) => (id ? list.find((o) => o.id === id)?.label || '…' : '');
